import { useState, useEffect, useCallback } from "react";
import { FileText, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Maximize2, Loader2, ExternalLink } from "lucide-react";
import { log } from "@/lib/log";

/**
 * Reusable receipt/invoice preview pane.
 * - Large preview with click-to-zoom full-screen lightbox (zoom + scroll-to-pan)
 * - Inline PDF rendering
 * - Thumbnail / prev-next navigation across multiple receipts
 * `fetchBlob(index)` returns a Promise<Blob> (authed or public, supplied by the parent).
 */
export function ReceiptViewer({ receipts, fetchBlob, testidPrefix = "receipt" }) {
  const [active, setActive] = useState(0);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(1);

  const count = receipts.length;
  const current = receipts[active] || null;
  const isImage = (current?.content_type || "").startsWith("image/");
  const isPdf = current?.content_type === "application/pdf";

  const go = useCallback((dir) => {
    setActive((i) => {
      const next = (i + dir + count) % count;
      return next;
    });
    setZoom(1);
  }, [count]);

  // Load the active receipt as a blob URL
  useEffect(() => {
    if (!current) return;
    let alive = true;
    let created;
    setUrl(null);
    setLoading(true);
    (async () => {
      try {
        const blob = await fetchBlob(active);
        if (!alive) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      } catch (err) {
        log.error("Failed to load receipt:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [active, current, fetchBlob]);

  // Keyboard: arrows navigate, Esc closes lightbox
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (count > 1 && e.key === "ArrowLeft") go(-1);
      if (count > 1 && e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, count]);

  if (count === 0) {
    return <div className="p-6 text-center text-ink-soft text-sm">No receipts attached.</div>;
  }

  const fname = current?.filename || `Receipt ${active + 1}`;

  return (
    <div data-testid={`${testidPrefix}-viewer`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-cream-200">
        <div className="text-[11px] uppercase tracking-wider text-ink-mute truncate">
          {count > 1 ? `${active + 1} / ${count} · ` : ""}{fname}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isImage && url && (
            <button
              type="button" onClick={() => { setZoom(1); setLightbox(true); }}
              data-testid={`${testidPrefix}-expand`} aria-label="Enlarge"
              className="h-8 w-8 grid place-items-center rounded-md text-forest-700 hover:bg-cream-100"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          {url && (
            <a
              href={url} target="_blank" rel="noreferrer"
              data-testid={`${testidPrefix}-open`} aria-label="Open in new tab"
              className="h-8 w-8 grid place-items-center rounded-md text-forest-700 hover:bg-cream-100"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>

      {/* Main preview pane */}
      <div className="relative bg-cream-100 h-[360px] sm:h-[460px] grid place-items-center overflow-hidden" data-testid={`${testidPrefix}-pane`}>
        {count > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Previous receipt"
              data-testid={`${testidPrefix}-prev`}
              className="absolute left-2 z-10 h-9 w-9 grid place-items-center rounded-full bg-white/90 border border-cream-200 text-forest-800 hover:bg-white shadow-sm">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next receipt"
              data-testid={`${testidPrefix}-next`}
              className="absolute right-2 z-10 h-9 w-9 grid place-items-center rounded-full bg-white/90 border border-cream-200 text-forest-800 hover:bg-white shadow-sm">
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {loading || !url ? (
          <Loader2 className="h-6 w-6 text-ink-mute animate-spin" />
        ) : isImage ? (
          <img
            src={url} alt={fname}
            onClick={() => { setZoom(1); setLightbox(true); }}
            className="max-h-full max-w-full object-contain cursor-zoom-in"
            data-testid={`${testidPrefix}-image`}
          />
        ) : isPdf ? (
          <iframe title={fname} src={url} className="w-full h-full border-0" data-testid={`${testidPrefix}-pdf`} />
        ) : (
          <div className="text-center text-ink-soft text-sm p-6">
            <FileText className="h-6 w-6 mx-auto mb-2 text-ink-mute" />
            {fname}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {count > 1 && (
        <div className="flex flex-wrap gap-1.5 px-4 py-3 border-t border-cream-200" data-testid={`${testidPrefix}-thumbs`}>
          {receipts.map((r, i) => (
            <button
              key={r.id || i} type="button" onClick={() => { setActive(i); setZoom(1); }}
              data-testid={`${testidPrefix}-thumb-${i}`}
              className={`h-8 min-w-8 px-2 rounded-md text-xs font-medium border transition-colors ${
                i === active
                  ? "bg-forest-700 text-cream-50 border-forest-700"
                  : "bg-white text-forest-800 border-cream-200 hover:bg-cream-100"
              }`}
              title={r.filename}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Full-screen lightbox */}
      {lightbox && isImage && url && (
        <div
          className="fixed inset-0 z-50 bg-ink/90 backdrop-blur-sm flex flex-col"
          data-testid={`${testidPrefix}-lightbox`}
          onClick={() => setLightbox(false)}
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 text-cream-50" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm truncate">{count > 1 ? `${active + 1} / ${count} · ` : ""}{fname}</div>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}
                aria-label="Zoom out" data-testid={`${testidPrefix}-zoom-out`}
                className="h-9 w-9 grid place-items-center rounded-md bg-white/10 hover:bg-white/20"><ZoomOut className="h-4 w-4" /></button>
              <span className="text-xs w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <button type="button" onClick={() => setZoom((z) => Math.min(4, +(z + 0.5).toFixed(1)))}
                aria-label="Zoom in" data-testid={`${testidPrefix}-zoom-in`}
                className="h-9 w-9 grid place-items-center rounded-md bg-white/10 hover:bg-white/20"><ZoomIn className="h-4 w-4" /></button>
              <button type="button" onClick={() => setLightbox(false)}
                aria-label="Close" data-testid={`${testidPrefix}-lightbox-close`}
                className="h-9 w-9 grid place-items-center rounded-md bg-white/10 hover:bg-white/20 ml-1"><X className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-start justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={url} alt={fname}
              style={{ width: `${zoom * 100}%`, maxWidth: zoom <= 1 ? "100%" : "none" }}
              className="h-auto select-none"
            />
          </div>
          {count > 1 && (
            <div className="flex items-center justify-center gap-6 pb-5 text-cream-50" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={() => go(-1)} aria-label="Previous"
                className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>
              <button type="button" onClick={() => go(1)} aria-label="Next"
                className="h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
